import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from '../../application/i18n/LocaleProvider';
import { CloseIcon, FitScreenIcon, ZoomInIcon } from '../icons/ControlIcons';
import PlantViewerSlide from './PlantViewerSlide';

/**
 * The picture, whole and then closer (SPEC-006, reframed by SPEC-008, reframed
 * again by SPEC-017).
 *
 * The hero is a band: it crops the scene and the photographs to fill itself, and
 * it hangs the plant's name, its category and the decision plate over what is
 * left. This is the same picture with none of that — the whole of it, fitted to
 * the screen on the plant's own wash so the specimen still multiplies into it
 * exactly as it does upstairs, and then as far into it as the reader wants to
 * pinch.
 *
 * A real `<dialog>` opened with `showModal()`, because focus trapping, Escape,
 * the top layer and an inert background are four things the platform gets right
 * and a hand-rolled overlay gets subtly wrong. The top layer is also what lets
 * this stay inside `.plant-hero`, which clips to its own bounds — no portal, and
 * `--plant-wash` still arrives by inheritance.
 *
 * Two framings have been tried here and this is the third, which is the first
 * one that does not choose for the reader: SPEC-006 fitted the picture and it
 * was small, SPEC-008 filled the screen and it was cropped, and the difference
 * now is that both are one gesture apart.
 */
export default function PlantLightbox({ slides, startIndex, plantName, onClose }) {
    const { t } = useLocale();
    const dialogRef = useRef(null);
    const railRef = useRef(null);
    // One imperative handle per slide, so the chrome up here can aim `reset` and
    // `toggleZoom` at whichever picture is showing without the view itself ever
    // reaching React (SPEC-017 D-4). Ref OBJECTS, allocated once per slide: an
    // inline callback ref would be a new function every render, and React
    // detaches and re-attaches a ref whose identity changed.
    const slideControls = useRef([]);
    const controlsFor = useCallback((index) => {
        slideControls.current[index] ??= { current: null };

        return slideControls.current[index];
    }, []);
    const [activeSlide, setActiveSlide] = useState(startIndex);
    // Which slide is zoomed, rather than a bare boolean: the button's label has
    // to describe the picture in front of the reader, and a slide reporting
    // itself fitted must not answer for the one they are actually looking at.
    const [zoomedSlide, setZoomedSlide] = useState(null);
    // The `close` listener is bound once, but has to report the slide the user
    // actually left on — a ref, so the binding never goes stale.
    const leftOnRef = useRef(startIndex);

    const trackScroll = useCallback((event) => {
        const rail = event.currentTarget;
        const index = Math.round(rail.scrollLeft / Math.max(rail.clientWidth, 1));

        leftOnRef.current = index;
        setActiveSlide(index);
    }, []);

    const onZoomChange = useCallback((index, fitted) => {
        setZoomedSlide((current) => {
            if (fitted) {
                return current === index ? null : current;
            }

            return index;
        });
    }, []);

    useEffect(() => {
        const dialog = dialogRef.current;
        // One exit for both ways out: the button calls `close()`, Escape makes
        // the browser call it, and this fires either way.
        const reportClosed = () => onClose(leftOnRef.current);

        dialog.addEventListener('close', reportClosed);
        dialog.showModal();
        // Opened ON the hero's picture rather than at the beginning: this is that
        // slide made whole, not a gallery. Set after `showModal()`, which is the
        // first moment the rail has a width to multiply.
        railRef.current.scrollLeft = startIndex * railRef.current.clientWidth;

        return () => {
            dialog.removeEventListener('close', reportClosed);

            if (dialog.open) {
                dialog.close();
            }
        };
    }, [startIndex, onClose]);

    // A zoom belongs to the picture it was made on (SPEC-017 D-6). Arriving on
    // one already inside it, at a position chosen for a different picture, would
    // make the reader's first move on every new slide an undo.
    useEffect(() => {
        slideControls.current.forEach((controls, index) => {
            if (index !== activeSlide) {
                controls.current?.reset();
            }
        });
    }, [activeSlide]);

    // A rotation re-fits every picture and moves every bound, so a view computed
    // against the old screen is not merely stale, it is out of range.
    useEffect(() => {
        const refit = () => slideControls.current.forEach((controls) => controls.current?.reset());

        window.addEventListener('resize', refit);

        return () => window.removeEventListener('resize', refit);
    }, []);

    const isFitted = zoomedSlide === null;

    return (
        <dialog
            className="plant-viewer"
            ref={dialogRef}
            aria-label={t('plants.detail.fullScreen', { plant: plantName })}
        >
            {/* First in the DOM so `showModal()` lands focus here, which is the
                one control someone who opened this by accident is looking for. */}
            <button
                type="button"
                className="round-button plant-viewer__close"
                onClick={() => dialogRef.current.close()}
                aria-label={t('plants.detail.closeFullScreen')}
            >
                <CloseIcon size={22} />
            </button>

            {/* The pinch's single-pointer equivalent, and the only VISIBLE thing
                that says this screen zooms at all (SPEC-017 D-5, NFR-04). */}
            <button
                type="button"
                className="round-button plant-viewer__zoom"
                onClick={() => slideControls.current[activeSlide]?.current?.toggleZoom()}
                aria-label={t(isFitted ? 'plants.detail.zoomIn' : 'plants.detail.fitToScreen')}
            >
                {isFitted ? <ZoomInIcon size={22} /> : <FitScreenIcon size={22} />}
            </button>

            <ul
                className="plant-viewer__rail"
                ref={railRef}
                onScroll={trackScroll}
                tabIndex={0}
                role="group"
                aria-label={t('plants.detail.galleryLabel', { plant: plantName })}
            >
                {slides.map((slide, index) => (
                    <PlantViewerSlide
                        key={slide.key}
                        slide={slide}
                        alt={t(slide.altKey, slide.altValues)}
                        index={index}
                        railRef={railRef}
                        controlsRef={controlsFor(index)}
                        onZoomChange={onZoomChange}
                    />
                ))}
            </ul>

            {slides.length > 1 ? (
                <ol className="plant-viewer__dots page-dots" aria-hidden="true">
                    {slides.map((slide, index) => (
                        <li
                            className={`page-dot${index === activeSlide ? ' page-dot--active' : ''}`}
                            key={slide.key}
                        />
                    ))}
                </ol>
            ) : null}
        </dialog>
    );
}
